import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { portfolio } from "@/data/siteContent";
import Img from "@/components/Img";
import SectionHeading from "@/components/SectionHeading";
import { ArrowUpRight } from "lucide-react";

/**
 * Internal flag for future case-study routes.
 * Keep false until `/work/:slug` (or equivalent) exists.
 * Flip to true and implement `openProject` — card markup stays the same.
 */
const CASE_STUDIES_ENABLED = false;

/**
 * Single entry point for opening an in-app project route.
 * Future: navigate(`/work/${project.slug}`) or router.push(…).
 * Requires stable `project.slug` from siteContent (already present on every project).
 */
function openProject(project) {
  if (!CASE_STUDIES_ENABLED) return;
  if (typeof window === "undefined" || !project?.slug) return;
  window.dispatchEvent(
    new CustomEvent("aurelia:open-project", { detail: { id: project.id, slug: project.slug } })
  );
}

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const tall = project.size === "tall";
  const externalHref = project.href && /^https?:\/\//.test(project.href) ? project.href : null;
  const caseStudyInteractive = CASE_STUDIES_ENABLED && !externalHref;
  const actionLabel = project.actionLabel || "View project";
  const statusLabel = project.statusLabel || "Selected Work";

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current.querySelector("[data-frame]"),
        { clipPath: "inset(0% 0% 100% 0%)" },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 1.3,
          ease: "power3.out",
          scrollTrigger: { trigger: ref.current, start: "top 88%" },
        }
      );
      gsap.fromTo(
        imgRef.current,
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: "none",
          scrollTrigger: {
            trigger: ref.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        }
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  const onMove = (e) => {
    if (prefersReducedMotion()) return;
    const rect = ref.current.getBoundingClientRect();
    const rx = ((e.clientY - rect.top) / rect.height - 0.5) * -5;
    const ry = ((e.clientX - rect.left) / rect.width - 0.5) * 5;
    gsap.to(ref.current.querySelector("[data-frame]"), {
      rotateX: rx,
      rotateY: ry,
      duration: 0.5,
      ease: "power2.out",
      transformPerspective: 1000,
    });
  };
  const onLeave = () =>
    gsap.to(ref.current.querySelector("[data-frame]"), {
      rotateX: 0,
      rotateY: 0,
      duration: 0.7,
      ease: "power2.out",
    });

  const frameClassName = "group relative block h-full w-full text-left";

  const frameInner = (
    <div
      data-frame
      className="relative h-full overflow-hidden rounded-[1.5rem] border border-white/10"
      style={{ willChange: "transform" }}
    >
      <div className={`h-full w-full overflow-hidden ${tall ? "min-h-[30rem]" : "min-h-[22rem]"}`}>
        <Img
          ref={imgRef}
          src={project.image}
          alt={`${project.title} — ${project.category}`}
          width={tall ? 900 : 1200}
          height={tall ? 1200 : 750}
          sizes="(max-width: 768px) 100vw, 50vw"
          className="h-[120%] w-full scale-105 object-cover grayscale-[0.4] transition-all duration-[900ms] ease-out group-hover:scale-110 group-hover:grayscale-0"
        />
      </div>

      {/* index + gold hairline that draws on hover */}
      <div className="pointer-events-none absolute left-6 top-6 z-10 flex items-center gap-3">
        <span className="font-mono text-xs text-white/70">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="h-px w-0 bg-gold transition-all duration-500 group-hover:w-10" />
      </div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-80" />

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6 md:p-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">{project.category}</p>
          <h3
            className="mt-2 font-display text-2xl tracking-tight text-white md:text-4xl"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 600 }}
          >
            {project.title}
          </h3>
          <div className="mt-3 flex items-center gap-3 text-xs text-white/60">
            <span className="font-mono">{project.year}</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            {externalHref || caseStudyInteractive ? (
              <span className="translate-y-1 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                {actionLabel}
              </span>
            ) : (
              <span className="text-white/45">{statusLabel}</span>
            )}
          </div>
        </div>
        {externalHref || caseStudyInteractive ? (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/25 text-white transition-all duration-500 group-hover:border-gold group-hover:bg-gold group-hover:text-black">
            <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:rotate-45" aria-hidden="true" />
          </span>
        ) : (
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 text-white/35"
            aria-hidden="true"
          >
            <ArrowUpRight size={18} />
          </span>
        )}
      </div>
    </div>
  );

  return (
    <article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={tall ? "md:row-span-2" : ""}
      data-testid={`portfolio-card-${project.id}`}
      data-project-id={project.id}
      data-project-slug={project.slug}
    >
      {externalHref ? (
        <a
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${actionLabel}: ${project.title}`}
          className={frameClassName}
          data-cursor="hover"
        >
          {frameInner}
        </a>
      ) : caseStudyInteractive ? (
        <button
          type="button"
          onClick={() => openProject(project)}
          aria-label={`View ${project.title} — ${project.category}`}
          className={frameClassName}
          data-cursor="hover"
        >
          {frameInner}
        </button>
      ) : (
        <div className={frameClassName}>{frameInner}</div>
      )}
    </article>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32"
      data-testid="portfolio-section"
    >
      <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow={portfolio.eyebrow}
          title={portfolio.heading}
          titleClassName="max-w-2xl lg:text-6xl"
        />
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/45">
          {String(portfolio.projects.length).padStart(2, "0")} projects
        </span>
      </div>

      <div className="grid grid-flow-row-dense auto-rows-auto grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
        {portfolio.projects.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} />
        ))}
      </div>
    </section>
  );
}
